import type { ReactNode } from "react";
import { Box } from "../../ui/box";
import { Button, ButtonText } from "../../ui/button";
import { ScrollView } from "../../ui/scroll-view";
import { Spinner } from "../../ui/spinner";
import MessageBubble from "./MessageBubble";
import type { AgentChatMessage } from "./types";
import useAgentScroll from "./useAgentScroll";

/** A form, a concept or an old question exchange: an object in the transcript, not a bubble. */
const isCard = (message: AgentChatMessage): boolean =>
  !!message.legacy?.answer || !!message.form || !!message.concept;

/**
 * Is the next message part of the same turn?
 *
 * A card is never grouped: it is a distinct object in the transcript, and tucking it
 * against a bubble makes it look like part of the message above it.
 */
const isGrouped = (
  message: AgentChatMessage,
  next: AgentChatMessage | undefined,
): boolean =>
  !!next && !isCard(message) && !isCard(next) && next.role === message.role;

type Props = {
  messages: AgentChatMessage[];
  /** The moment card, streaming bubble and error bubble — whatever is live right now. */
  overlay?: ReactNode;
  hasOlder: boolean;
  loadingOlder: boolean;
  onLoadOlder: () => void;
  scroll: ReturnType<typeof useAgentScroll>;
};

/**
 * The transcript.
 *
 * A normal (not inverted) scroll view. Inverted lists are the usual chat trick, but
 * on React Native Web they are a CSS flip that breaks text selection — and copying
 * the agent's answer out is something people will absolutely want to do.
 *
 * This does not re-render while the answer streams: the token buffer lives outside
 * React and only `StreamingBubble` subscribes to it.
 */
const MessageList = ({
  messages,
  overlay,
  hasOlder,
  loadingOlder,
  onLoadOlder,
  scroll,
}: Props) => (
  <ScrollView
    ref={scroll.ref}
    className="flex-1 px-1 pt-2 md:px-4 md:pt-4"
    onScroll={scroll.onScroll}
    scrollEventThrottle={16}
    onContentSizeChange={() => scroll.scrollToBottom()}
  >
    {hasOlder ? (
      <Box className="mb-3 items-center">
        <Button
          variant="link"
          size="sm"
          onPress={onLoadOlder}
          isDisabled={loadingOlder}
        >
          {loadingOlder ? (
            <Spinner size="small" />
          ) : (
            <ButtonText>Ver mensajes anteriores</ButtonText>
          )}
        </Button>
      </Box>
    ) : null}

    {messages.map((message, index) => (
      <MessageBubble
        key={message.id}
        message={message}
        // Consecutive messages from the same side belong to one turn, so they read as
        // one thing: the gap tightens and only the last keeps its tail. Computed here
        // because a bubble cannot see its neighbours.
        grouped={isGrouped(message, messages[index + 1])}
      />
    ))}

    {overlay}
  </ScrollView>
);

export default MessageList;
